import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, HashIcon, PhoneIcon, HomeIcon, ClockIcon, CheckCircleIcon, CalendarIcon, MapPinIcon, EuroIcon, BuildingIcon, PlusIcon, EyeIcon, AlertCircleIcon, UserIcon, DollarSignIcon, EditIcon, SaveIcon } from 'lucide-react';
import { useRental } from '../../contexts/RentalContext';
interface BuyerLead {
  leadId: string;
  name: string;
  phone: string;
  propertyId?: string;
  entryDate: string;
  entryTime: string;
  callCenterMemo: string;
  fullName?: string;
  propertyType?: string;
  rooms?: number;
  budgetMin?: number;
  budgetMax?: number;
  zones: string[];
  excludedFloors?: string;
  maxFloors?: number;
  bathrooms?: number;
  constructionYear?: number;
  qualificationMemo?: string;
  qualified: boolean;
  leadSource?: string;
  actionHistory: Array<{
    action: string;
    timestamp: string;
    agent: string;
  }>;
  whoWillMove?: string;
  acquisitionReason?: string;
  paymentMethods?: string[];
}
interface PropertyViewing {
  id?: string;
  propertyId: string;
  ownerName: string;
  ownerPhone: string;
  status: 'none' | 'scheduled' | 'completed' | 'cancelled';
  scheduledDate?: string;
  scheduledTime?: string;
  completedDate?: string;
  commissionPaidBy?: 'owner' | 'client' | 'both';
  ownerCommission?: number;
  clientCommission?: number;
  notes?: string;
}
interface QualifiedLeadDetailsPanelProps {
  lead: BuyerLead;
  onClose: () => void;
  onUpdateLead?: (leadId: string, updates: Partial<BuyerLead>) => void;
}
export function QualifiedLeadDetailsPanel({
  lead,
  onClose,
  onUpdateLead
}: QualifiedLeadDetailsPanelProps) {
  const {
    addViewing,
    updateViewing,
    viewings: contextViewings
  } = useRental();
  // Qualification editing state
  const [isEditingQualification, setIsEditingQualification] = useState(false);
  const [editedLead, setEditedLead] = useState<BuyerLead>(lead);
  // Viewing editing state
  const [editingViewingId, setEditingViewingId] = useState<string | null>(null);
  const [editedViewing, setEditedViewing] = useState<PropertyViewing | null>(null);
  // Get viewings for this lead from context
  const leadViewings = contextViewings.filter(v => v.leadId === lead.leadId);
  const [viewings, setViewings] = useState<PropertyViewing[]>([...leadViewings.map(v => ({
    id: v.id,
    propertyId: v.propertyId,
    ownerName: v.ownerName,
    ownerPhone: v.ownerPhone,
    status: v.status as 'none' | 'scheduled' | 'completed' | 'cancelled',
    scheduledDate: v.date,
    scheduledTime: v.time,
    commissionPaidBy: v.commissionPaidBy,
    ownerCommission: v.ownerCommission,
    clientCommission: v.clientCommission,
    notes: v.agentNotes
  })), ...(lead.propertyId && !leadViewings.find(v => v.propertyId === lead.propertyId) ? [{
    propertyId: lead.propertyId,
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    status: 'none' as const
  }] : [])]);
  const [isSchedulingViewing, setIsSchedulingViewing] = useState(false);
  const [newViewingPropertyId, setNewViewingPropertyId] = useState('');
  const [newViewingOwnerName, setNewViewingOwnerName] = useState('');
  const [newViewingOwnerPhone, setNewViewingOwnerPhone] = useState('');
  const [newViewingDate, setNewViewingDate] = useState('');
  const [newViewingTime, setNewViewingTime] = useState('');
  const [newViewingCommission, setNewViewingCommission] = useState<'owner' | 'client' | 'both'>('owner');
  const [newViewingOwnerCommission, setNewViewingOwnerCommission] = useState('');
  const [newViewingClientCommission, setNewViewingClientCommission] = useState('');
  const [newViewingNotes, setNewViewingNotes] = useState('');
  const handleSaveQualification = () => {
    if (!editedLead.fullName || !editedLead.propertyType || !editedLead.rooms || !editedLead.budgetMin || !editedLead.budgetMax || editedLead.zones.length < 3) {
      alert('Te rog completează toate câmpurile obligatorii (inclusiv minim 3 zone)');
      return;
    }
    if (onUpdateLead) {
      onUpdateLead(lead.leadId, editedLead);
    }
    setIsEditingQualification(false);
    alert('✅ Calificare actualizată cu succes!');
  };
  const handleStartEditViewing = (viewing: PropertyViewing) => {
    setEditingViewingId(viewing.id || null);
    setEditedViewing({
      ...viewing
    });
  };
  const handleSaveViewing = () => {
    if (!editedViewing) return;
    if (!editedViewing.propertyId || !editedViewing.ownerName || !editedViewing.ownerPhone) {
      alert('Te rog completează toate câmpurile obligatorii');
      return;
    }
    if (editedViewing.status === 'scheduled') {
      if (!editedViewing.scheduledDate || !editedViewing.scheduledTime) {
        alert('Te rog completează data și ora pentru vizionarea programată');
        return;
      }
      // Validate commission
      if (editedViewing.commissionPaidBy === 'owner' && !editedViewing.ownerCommission) {
        alert('Te rog introdu valoarea comisionului pentru proprietar');
        return;
      }
      if (editedViewing.commissionPaidBy === 'client' && !editedViewing.clientCommission) {
        alert('Te rog introdu valoarea comisionului pentru client');
        return;
      }
      if (editedViewing.commissionPaidBy === 'both' && (!editedViewing.ownerCommission || !editedViewing.clientCommission)) {
        alert('Te rog introdu valorile comisionului pentru ambele părți');
        return;
      }
    }
    // Update in context if it has an ID
    if (editedViewing.id) {
      updateViewing(editedViewing.id, {
        propertyId: editedViewing.propertyId,
        ownerName: editedViewing.ownerName,
        ownerPhone: editedViewing.ownerPhone,
        date: editedViewing.scheduledDate || '',
        time: editedViewing.scheduledTime || '',
        status: editedViewing.status,
        commissionPaidBy: editedViewing.commissionPaidBy || 'owner',
        ownerCommission: editedViewing.ownerCommission,
        clientCommission: editedViewing.clientCommission,
        agentNotes: editedViewing.notes || ''
      });
    }
    // Update local state
    setViewings(viewings.map(v => v.id === editingViewingId ? editedViewing : v));
    setEditingViewingId(null);
    setEditedViewing(null);
    alert('✅ Vizionare actualizată cu succes!');
  };
  const handleScheduleViewing = () => {
    if (!newViewingPropertyId || !newViewingOwnerName || !newViewingOwnerPhone || !newViewingDate || !newViewingTime) {
      alert('Te rog completează toate câmpurile obligatorii');
      return;
    }
    // Validate commission values based on selection
    if (newViewingCommission === 'owner' && !newViewingOwnerCommission) {
      alert('Te rog introdu valoarea comisionului pentru proprietar');
      return;
    }
    if (newViewingCommission === 'client' && !newViewingClientCommission) {
      alert('Te rog introdu valoarea comisionului pentru client');
      return;
    }
    if (newViewingCommission === 'both' && (!newViewingOwnerCommission || !newViewingClientCommission)) {
      alert('Te rog introdu valorile comisionului pentru ambele părți');
      return;
    }
    // Generate unique ID for viewing
    const viewingId = `V${Date.now()}`;
    // Create viewing object for context
    const newViewingForContext = {
      id: viewingId,
      leadId: lead.leadId,
      clientName: lead.fullName || lead.name,
      clientPhone: lead.phone,
      propertyId: newViewingPropertyId,
      ownerName: newViewingOwnerName,
      ownerPhone: newViewingOwnerPhone,
      date: newViewingDate,
      time: newViewingTime,
      duration: 30,
      status: 'scheduled' as const,
      meetingPoint: 'La proprietate',
      commissionPaidBy: newViewingCommission,
      ownerCommission: newViewingOwnerCommission ? parseFloat(newViewingOwnerCommission) : undefined,
      clientCommission: newViewingClientCommission ? parseFloat(newViewingClientCommission) : undefined,
      agentNotes: newViewingNotes,
      createdAt: new Date().toISOString()
    };
    addViewing(newViewingForContext);
    const newViewing: PropertyViewing = {
      id: viewingId,
      propertyId: newViewingPropertyId,
      ownerName: newViewingOwnerName,
      ownerPhone: newViewingOwnerPhone,
      status: 'scheduled',
      scheduledDate: newViewingDate,
      scheduledTime: newViewingTime,
      commissionPaidBy: newViewingCommission,
      ownerCommission: newViewingOwnerCommission ? parseFloat(newViewingOwnerCommission) : undefined,
      clientCommission: newViewingClientCommission ? parseFloat(newViewingClientCommission) : undefined,
      notes: newViewingNotes
    };
    setViewings([...viewings, newViewing]);
    // Reset form
    setNewViewingPropertyId('');
    setNewViewingOwnerName('');
    setNewViewingOwnerPhone('');
    setNewViewingDate('');
    setNewViewingTime('');
    setNewViewingCommission('owner');
    setNewViewingOwnerCommission('');
    setNewViewingClientCommission('');
    setNewViewingNotes('');
    setIsSchedulingViewing(false);
    alert('✅ Vizionare programată cu succes!');
  };
  const getViewingStatusBadge = (status: PropertyViewing['status']) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center space-x-1" data-id="element-4969">
            <CalendarIcon className="w-3 h-3" data-id="element-4970" />
            <span data-id="element-4971">Programată</span>
          </span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center space-x-1" data-id="element-4972">
            <CheckCircleIcon className="w-3 h-3" data-id="element-4973" />
            <span data-id="element-4974">Finalizată</span>
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center space-x-1" data-id="element-4975">
            <AlertCircleIcon className="w-3 h-3" data-id="element-4976" />
            <span data-id="element-4977">Anulată</span>
          </span>;
      default:
        return null;
    }
  };
  return <>
      {/* Backdrop with higher z-index */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100" style={{
      zIndex: 100
    }} data-id="element-4978" />

      {/* Sidebar Panel - FIXED: starts below navbar */}
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed right-0 top-16 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-110 overflow-y-auto" style={{
      zIndex: 110
    }} data-id="element-4979">
        {/* Header with gradient - NO LONGER STICKY, just at top of scrollable content */}
        <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg" data-id="element-4980">
          <div className="flex items-center justify-between" data-id="element-4981">
            <div data-id="element-4982">
              <div className="flex items-center space-x-2 mb-2" data-id="element-4983">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-4984">
                  <UserIcon className="w-5 h-5 text-white" data-id="element-4985" />
                </div>
                <div data-id="element-4986">
                  <h3 className="text-xl font-bold" data-id="element-4987">Lead Calificat</h3>
                  <p className="text-sm text-blue-200" data-id="element-4988">Detalii Complete</p>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-4989">
              <XIcon className="w-6 h-6 text-white" data-id="element-4990" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6" data-id="element-4991">
          {/* Lead Info Card - matching dashboard card style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4992">
            <div className="flex items-start justify-between mb-4" data-id="element-4993">
              <div className="flex items-center space-x-3" data-id="element-4994">
                <div className="w-12 h-12 bg-gradient-to-br from-[#21A366] to-[#1a8555] rounded-xl flex items-center justify-center" data-id="element-4995">
                  <HashIcon className="w-6 h-6 text-white" data-id="element-4996" />
                </div>
                <div data-id="element-4997">
                  <div className="flex items-center space-x-2 mb-1" data-id="element-4998">
                    <span className="text-lg font-bold text-[#132C53]" data-id="element-4999">
                      {lead.leadId}
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold" data-id="element-5000">
                      ✓ Calificat
                    </span>
                  </div>
                  <h4 className="text-2xl font-bold text-[#132C53]" data-id="element-5001">
                    {lead.fullName || lead.name}
                  </h4>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-5002">
              <div className="flex items-center space-x-2 text-sm" data-id="element-5003">
                <PhoneIcon className="w-4 h-4 text-blue-500" data-id="element-5004" />
                <span className="text-slate-600" data-id="element-5005">{lead.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm" data-id="element-5006">
                <ClockIcon className="w-4 h-4 text-purple-500" data-id="element-5007" />
                <span className="text-slate-600" data-id="element-5008">
                  {lead.entryDate} {lead.entryTime}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Qualification Details - WITH EDIT - matching dashboard style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-5009">
            <div className="flex items-center justify-between mb-5" data-id="element-5010">
              <h5 className="text-lg font-bold text-[#132C53] flex items-center space-x-2" data-id="element-5011">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-5012">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-5013" />
                </div>
                <span data-id="element-5014">Criterii de Calificare</span>
              </h5>
              {!isEditingQualification ? <button onClick={() => {
              setIsEditingQualification(true);
              setEditedLead(lead);
            }} className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all" data-id="element-5015">
                  <EditIcon className="w-4 h-4" data-id="element-5016" />
                  <span data-id="element-5017">Editează</span>
                </button> : <div className="flex space-x-2" data-id="element-5018">
                  <button onClick={handleSaveQualification} className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all" data-id="element-5019">
                    <SaveIcon className="w-4 h-4" data-id="element-5020" />
                    <span data-id="element-5021">Salvează</span>
                  </button>
                  <button onClick={() => {
                setIsEditingQualification(false);
                setEditedLead(lead);
              }} className="px-3 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-300 transition-colors" data-id="element-5022">
                    Anulează
                  </button>
                </div>}
            </div>

            {!isEditingQualification ? <>
                {/* View Mode - matching dashboard card grid */}
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-5023">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5024">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5025">
                      Tip Imobil
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-5026">
                      {lead.propertyType}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5027">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5028">
                      Nr. Camere
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-5029">{lead.rooms}</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-5030">
                    <p className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-5031">
                      Buget
                    </p>
                    <p className="font-bold text-emerald-700" data-id="element-5032">
                      {lead.budgetMin}-{lead.budgetMax} EUR
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-5033">
                    <p className="text-xs text-blue-600 mb-1 font-medium" data-id="element-5034">
                      Suprafață
                    </p>
                    <p className="font-bold text-blue-700" data-id="element-5035">
                      {lead.surfaceMin}-{lead.surfaceMax} m²
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5036">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5037">
                      Zone
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-5038">
                      {lead.zones.length}
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-3 border border-slate-200" data-id="element-5039">
                  <p className="text-xs text-slate-500 mb-2 font-medium" data-id="element-5040">
                    Zone Preferate
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-5041">
                    {lead.zones.map((zone, idx) => <span key={idx} className="px-3 py-1 bg-white border border-emerald-200 text-emerald-700 rounded-full text-xs font-semibold shadow-sm" data-id="element-5042">
                        {zone}
                      </span>)}
                  </div>
                </div>

                {/* Payment Methods */}
                {lead.paymentMethods && lead.paymentMethods.length > 0 && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-3 border border-slate-200" data-id="element-5043">
                    <p className="text-xs text-slate-500 mb-2 font-medium" data-id="element-5044">
                      Modalitate de Plată
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-5045">
                      {lead.paymentMethods.map((method, idx) => <span key={idx} className="px-3 py-1 bg-emerald-100 border border-emerald-300 text-emerald-700 rounded-full text-xs font-bold" data-id="element-5046">
                          {method === 'cash' ? '💵 Cash' : '💳 Credit'}
                        </span>)}
                    </div>
                  </div>}

                {/* Keep existing optional fields and memo sections */}
                {(lead.excludedFloors || lead.maxFloors || lead.bathrooms || lead.constructionYear) && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-5047">
                    <p className="text-sm font-semibold text-slate-700 mb-3" data-id="element-5048">
                      Criterii Suplimentare
                    </p>
                    <div className="grid grid-cols-2 gap-2" data-id="element-5049">
                      {lead.excludedFloors && <div className="bg-white rounded p-2" data-id="element-5050">
                          <span className="text-gray-500" data-id="element-5051">Etaje excluse:</span>
                          <span className="font-semibold ml-1" data-id="element-5052">
                            {lead.excludedFloors}
                          </span>
                        </div>}
                      {lead.maxFloors && <div className="bg-white rounded p-2" data-id="element-5053">
                          <span className="text-gray-500" data-id="element-5054">Max etaje:</span>
                          <span className="font-semibold ml-1" data-id="element-5055">
                            {lead.maxFloors}
                          </span>
                        </div>}
                      {lead.bathrooms && <div className="bg-white rounded p-2" data-id="element-5056">
                          <span className="text-gray-500" data-id="element-5057">Băi:</span>
                          <span className="font-semibold ml-1" data-id="element-5058">
                            {lead.bathrooms}
                          </span>
                        </div>}
                      {lead.constructionYear && <div className="bg-white rounded p-2" data-id="element-5059">
                          <span className="text-gray-500" data-id="element-5060">An construcție:</span>
                          <span className="font-semibold ml-1" data-id="element-5061">
                            {lead.constructionYear}
                          </span>
                        </div>}
                    </div>
                  </div>}

                {lead.qualificationMemo && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5062">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5063">
                      Memo Calificare
                    </p>
                    <p className="text-sm text-slate-700" data-id="element-5064">
                      {lead.qualificationMemo}
                    </p>
                  </div>}

                {(lead.whoWillMove || lead.acquisitionReason) && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-5065">
                    <p className="text-sm font-semibold text-slate-700 mb-3" data-id="element-5066">
                      Întrebări Calificare
                    </p>
                    <div className="space-y-3" data-id="element-5067">
                      {lead.whoWillMove && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5068">
                          <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5069">
                            Cine se va muta în noul imobil?
                          </p>
                          <p className="text-sm text-slate-700" data-id="element-5070">
                            {lead.whoWillMove}
                          </p>
                        </div>}
                      {lead.acquisitionReason && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5071">
                          <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-5072">
                            De ce se dorește o altă achiziție de imobil?
                          </p>
                          <p className="text-sm text-slate-700" data-id="element-5073">
                            {lead.acquisitionReason}
                          </p>
                        </div>}
                    </div>
                  </div>}
              </> : <>
                {/* Edit Mode - UPDATED to match new qualification form */}
                <div className="space-y-4" data-id="element-5074">
                  {/* Contact Info */}
                  <div data-id="element-5075">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5076">
                      Nume Complet <span className="text-red-500" data-id="element-5077">*</span>
                    </label>
                    <input type="text" value={editedLead.fullName || ''} onChange={e => setEditedLead({
                  ...editedLead,
                  fullName: e.target.value
                })} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm" placeholder="ex: Popescu Ion" data-id="element-5078" />
                  </div>

                  {/* Property Requirements */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5079">
                    <div data-id="element-5080">
                      <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5081">
                        Tip Imobil
                      </label>
                      <select value={editedLead.propertyType || ''} onChange={e => setEditedLead({
                    ...editedLead,
                    propertyType: e.target.value
                  })} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm" data-id="element-5082">
                        <option value="" data-id="element-5083">Selectează...</option>
                        <option value="Apartament" data-id="element-5084">Apartament</option>
                        <option value="Casă" data-id="element-5085">Casă</option>
                        <option value="Vilă" data-id="element-5086">Vilă</option>
                        <option value="Penthouse" data-id="element-5087">Penthouse</option>
                        <option value="Studio" data-id="element-5088">Studio</option>
                      </select>
                    </div>
                    <div data-id="element-5089">
                      <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5090">
                        Nr. Camere <span className="text-red-500" data-id="element-5091">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-5092">
                        {[1, 2, 3, 4, '4+'].map(opt => <button key={opt} type="button" onClick={() => setEditedLead({
                      ...editedLead,
                      rooms: typeof opt === 'number' ? opt : 4
                    })} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${editedLead.rooms === (typeof opt === 'number' ? opt : 4) ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-5093">
                            {opt}
                          </button>)}
                      </div>
                    </div>
                  </div>

                  {/* Budget */}
                  <div data-id="element-5094">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5095">
                      Buget (EUR) <span className="text-red-500" data-id="element-5096">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5097">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5098">
                        <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-5099">
                          Minim
                        </label>
                        <input type="number" value={editedLead.budgetMin || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      budgetMin: parseInt(e.target.value)
                    })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm" placeholder="400" data-id="element-5100" />
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-5101">
                        <label className="block text-xs text-emerald-600 mb-2 font-medium" data-id="element-5102">
                          Maxim
                        </label>
                        <input type="number" value={editedLead.budgetMax || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      budgetMax: parseInt(e.target.value)
                    })} className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm" placeholder="500" data-id="element-5103" />
                      </div>
                    </div>
                  </div>

                  {/* Surface Area */}
                  <div data-id="element-5104">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5105">
                      Suprafață (m²) <span className="text-red-500" data-id="element-5106">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5107">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5108">
                        <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-5109">
                          Minim
                        </label>
                        <input type="number" value={editedLead.surfaceMin || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      surfaceMin: parseInt(e.target.value)
                    })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-sm" placeholder="50" data-id="element-5110" />
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-5111">
                        <label className="block text-xs text-blue-600 mb-2 font-medium" data-id="element-5112">
                          Maxim
                        </label>
                        <input type="number" value={editedLead.surfaceMax || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      surfaceMax: parseInt(e.target.value)
                    })} className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-sm" placeholder="75" data-id="element-5113" />
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div data-id="element-5114">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5115">
                      Modalitate de Plată
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-5116">
                      <button type="button" onClick={() => {
                    const methods = editedLead.paymentMethods || [];
                    setEditedLead({
                      ...editedLead,
                      paymentMethods: methods.includes('cash') ? methods.filter(m => m !== 'cash') : [...methods, 'cash']
                    });
                  }} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${(editedLead.paymentMethods || []).includes('cash') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-5117">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${(editedLead.paymentMethods || []).includes('cash') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-5118">
                          {(editedLead.paymentMethods || []).includes('cash') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-5119" />}
                        </div>
                        <span data-id="element-5120">Cash</span>
                      </button>
                      <button type="button" onClick={() => {
                    const methods = editedLead.paymentMethods || [];
                    setEditedLead({
                      ...editedLead,
                      paymentMethods: methods.includes('credit') ? methods.filter(m => m !== 'credit') : [...methods, 'credit']
                    });
                  }} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${(editedLead.paymentMethods || []).includes('credit') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-5121">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${(editedLead.paymentMethods || []).includes('credit') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-5122">
                          {(editedLead.paymentMethods || []).includes('credit') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-5123" />}
                        </div>
                        <span data-id="element-5124">Credit</span>
                      </button>
                    </div>
                  </div>

                  {/* Zones */}
                  <div data-id="element-5125">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5126">
                      Zone Preferate (minim 3){' '}
                      <span className="text-red-500" data-id="element-5127">*</span>
                    </label>
                    <div className="space-y-2" data-id="element-5128">
                      {[0, 1, 2, 3, 4].map(idx => <input key={idx} type="text" placeholder={`Zona ${idx + 1}${idx < 3 ? ' (obligatoriu)' : ' (opțional)'}`} value={editedLead.zones[idx] || ''} onChange={e => {
                    const newZones = [...editedLead.zones];
                    newZones[idx] = e.target.value;
                    setEditedLead({
                      ...editedLead,
                      zones: newZones.filter(z => z)
                    });
                  }} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm" data-id="element-5129" />)}
                    </div>
                  </div>

                  {/* Optional Fields Section */}
                  <div className="border-t border-slate-200 pt-4" data-id="element-5130">
                    <h6 className="text-sm font-bold text-slate-700 mb-3" data-id="element-5131">
                      Câmpuri Opționale
                    </h6>

                    <div className="space-y-4" data-id="element-5132">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5133">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5134">
                          Etaje Excluse
                        </label>
                        <input type="text" value={editedLead.excludedFloors || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      excludedFloors: e.target.value
                    })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white text-sm" placeholder="ex: parter, etaj 1" data-id="element-5135" />
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5136">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5137">
                          Nr. Maxim de Etaje
                        </label>
                        <input type="number" value={editedLead.maxFloors || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      maxFloors: parseInt(e.target.value)
                    })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white text-sm" placeholder="ex: 5" data-id="element-5138" />
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5139">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5140">
                          Nr. Băi
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-5141">
                          {[1, 2, 3, 4, '4+'].map(opt => <button key={opt} type="button" onClick={() => setEditedLead({
                        ...editedLead,
                        bathrooms: typeof opt === 'number' ? opt : 4
                      })} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${editedLead.bathrooms === (typeof opt === 'number' ? opt : 4) ? 'bg-slate-600 text-white border-slate-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-5142">
                              {opt}
                            </button>)}
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5143">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5144">
                          An Construcție
                        </label>
                        <input type="number" value={editedLead.constructionYear || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      constructionYear: parseInt(e.target.value)
                    })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white text-sm" placeholder="ex: 2015" data-id="element-5145" />
                      </div>
                    </div>
                  </div>

                  {/* Qualification Questions */}
                  <div className="border-t border-slate-200 pt-4" data-id="element-5146">
                    <h6 className="text-sm font-bold text-slate-700 mb-3" data-id="element-5147">
                      Întrebări Calificare
                    </h6>

                    <div className="space-y-4" data-id="element-5148">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5149">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5150">
                          Cine se va muta în noul imobil?
                        </label>
                        <input type="text" value={editedLead.whoWillMove || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      whoWillMove: e.target.value
                    })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white text-sm" placeholder="ex: Familie cu 2 copii, Persoană singură..." data-id="element-5151" />
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5152">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5153">
                          De ce se dorește o altă achiziție de imobil?
                        </label>
                        <textarea value={editedLead.acquisitionReason || ''} onChange={e => setEditedLead({
                      ...editedLead,
                      acquisitionReason: e.target.value
                    })} rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none bg-white text-sm" placeholder="ex: Mutare în alt oraș, Spațiu mai mare..." data-id="element-5154" />
                      </div>
                    </div>
                  </div>

                  {/* Memo */}
                  <div data-id="element-5155">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-5156">
                      Memo Calificare
                    </label>
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5157">
                      <textarea rows={3} value={editedLead.qualificationMemo || ''} onChange={e => setEditedLead({
                    ...editedLead,
                    qualificationMemo: e.target.value
                  })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none bg-white text-sm" placeholder="Alte detalii relevante despre client..." data-id="element-5158" />
                    </div>
                  </div>
                </div>
              </>}
          </motion.div>

          {/* Lead Source */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-5159">
            <h5 className="text-lg font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-5160">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-5161">
                <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-5162" />
              </div>
              <span data-id="element-5163">Surse Lead</span>
            </h5>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-5164">
              <p className="text-xs text-emerald-600 mb-2 font-medium" data-id="element-5165">Sursă</p>
              {lead.propertyId ? <div className="flex items-center space-x-2" data-id="element-5166">
                  <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-5167" />
                  <span className="font-bold text-emerald-700 text-lg" data-id="element-5168">
                    {lead.propertyId}
                  </span>
                </div> : <div className="flex items-center space-x-2" data-id="element-5169">
                  <AlertCircleIcon className="w-5 h-5 text-slate-600" data-id="element-5170" />
                  <span className="font-bold text-slate-700 text-lg" data-id="element-5171">
                    {lead.leadSource || 'generat-suport'}
                  </span>
                </div>}
            </div>
          </motion.div>

          {/* Viewings Section - WITH EDIT - matching dashboard style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-5172">
            <div className="flex items-center justify-between mb-5" data-id="element-5173">
              <h5 className="text-lg font-bold text-[#132C53] flex items-center space-x-2" data-id="element-5174">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-5175">
                  <EyeIcon className="w-5 h-5 text-blue-600" data-id="element-5176" />
                </div>
                <span data-id="element-5177">Status Vizionări</span>
              </h5>
              <button onClick={() => setIsSchedulingViewing(true)} className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all" data-id="element-5178">
                <PlusIcon className="w-4 h-4" data-id="element-5179" />
                <span data-id="element-5180">Programează</span>
              </button>
            </div>

            {/* Existing Viewings - keep existing code with updated card styling */}
            <div className="space-y-3" data-id="element-5181">
              {viewings.map((viewing, idx) => <motion.div key={viewing.id || idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all" data-id="element-5182">
                  {editingViewingId === viewing.id && editedViewing /* Edit Mode for Viewing */ ? <div className="space-y-3" data-id="element-5183">
                      <div className="flex items-center justify-between mb-2" data-id="element-5184">
                        <h6 className="font-bold text-gray-900 text-sm" data-id="element-5185">
                          Editează Vizionare
                        </h6>
                        <div className="flex space-x-2" data-id="element-5186">
                          <button onClick={handleSaveViewing} className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700" data-id="element-5187">
                            <SaveIcon className="w-3 h-3" data-id="element-5188" />
                            <span data-id="element-5189">Salvează</span>
                          </button>
                          <button onClick={() => {
                      setEditingViewingId(null);
                      setEditedViewing(null);
                    }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-bold hover:bg-gray-300" data-id="element-5190">
                            Anulează
                          </button>
                        </div>
                      </div>

                      <div data-id="element-5191">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5192">
                          ID Proprietate *
                        </label>
                        <input type="text" value={editedViewing.propertyId} onChange={e => setEditedViewing({
                    ...editedViewing,
                    propertyId: e.target.value
                  })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5193" />
                      </div>

                      <div className="grid grid-cols-2 gap-2" data-id="element-5194">
                        <div data-id="element-5195">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5196">
                            Nume Proprietar *
                          </label>
                          <input type="text" value={editedViewing.ownerName} onChange={e => setEditedViewing({
                      ...editedViewing,
                      ownerName: e.target.value
                    })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5197" />
                        </div>
                        <div data-id="element-5198">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5199">
                            Telefon Proprietar *
                          </label>
                          <input type="tel" value={editedViewing.ownerPhone} onChange={e => setEditedViewing({
                      ...editedViewing,
                      ownerPhone: e.target.value
                    })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5200" />
                        </div>
                      </div>

                      <div data-id="element-5201">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5202">
                          Status
                        </label>
                        <select value={editedViewing.status} onChange={e => setEditedViewing({
                    ...editedViewing,
                    status: e.target.value as any
                  })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5203">
                          <option value="none" data-id="element-5204">Fără vizionare</option>
                          <option value="scheduled" data-id="element-5205">Programată</option>
                          <option value="completed" data-id="element-5206">Finalizată</option>
                          <option value="cancelled" data-id="element-5207">Anulată</option>
                        </select>
                      </div>

                      {editedViewing.status === 'scheduled' && <>
                          <div className="grid grid-cols-2 gap-2" data-id="element-5208">
                            <div data-id="element-5209">
                              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5210">
                                Data *
                              </label>
                              <input type="date" value={editedViewing.scheduledDate || ''} onChange={e => setEditedViewing({
                        ...editedViewing,
                        scheduledDate: e.target.value
                      })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5211" />
                            </div>
                            <div data-id="element-5212">
                              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5213">
                                Ora *
                              </label>
                              <input type="time" value={editedViewing.scheduledTime || ''} onChange={e => setEditedViewing({
                        ...editedViewing,
                        scheduledTime: e.target.value
                      })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5214" />
                            </div>
                          </div>

                          <div data-id="element-5215">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5216">
                              Comisionare
                            </label>
                            <div className="grid grid-cols-3 gap-1" data-id="element-5217">
                              {(['owner', 'client', 'both'] as const).map(type => <button key={type} type="button" onClick={() => setEditedViewing({
                        ...editedViewing,
                        commissionPaidBy: type
                      })} className={`px-2 py-1.5 rounded text-xs font-bold transition-all ${editedViewing.commissionPaidBy === type ? type === 'owner' ? 'bg-purple-600 text-white' : type === 'client' ? 'bg-blue-600 text-white' : 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-5218">
                                    {type === 'owner' ? 'Proprietar' : type === 'client' ? 'Client' : 'Ambele'}
                                  </button>)}
                            </div>
                          </div>

                          {(editedViewing.commissionPaidBy === 'owner' || editedViewing.commissionPaidBy === 'both') && <div data-id="element-5219">
                              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5220">
                                Comision Proprietar (%)
                              </label>
                              <input type="number" step="0.5" min="0" max="100" value={editedViewing.ownerCommission || ''} onChange={e => setEditedViewing({
                      ...editedViewing,
                      ownerCommission: parseFloat(e.target.value)
                    })} className="w-full px-2 py-1.5 border border-purple-300 rounded text-sm bg-purple-50" data-id="element-5221" />
                            </div>}

                          {(editedViewing.commissionPaidBy === 'client' || editedViewing.commissionPaidBy === 'both') && <div data-id="element-5222">
                              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5223">
                                Comision Client (%)
                              </label>
                              <input type="number" step="0.5" min="0" max="100" value={editedViewing.clientCommission || ''} onChange={e => setEditedViewing({
                      ...editedViewing,
                      clientCommission: parseFloat(e.target.value)
                    })} className="w-full px-2 py-1.5 border border-blue-300 rounded text-sm bg-blue-50" data-id="element-5224" />
                            </div>}
                        </>}

                      <div data-id="element-5225">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5226">
                          Notițe
                        </label>
                        <textarea rows={2} value={editedViewing.notes || ''} onChange={e => setEditedViewing({
                    ...editedViewing,
                    notes: e.target.value
                  })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-id="element-5227" />
                      </div>
                    </div> /* View Mode for Viewing */ : <>
                      <div className="flex items-start justify-between mb-3" data-id="element-5228">
                        <div className="flex items-center space-x-2" data-id="element-5229">
                          <HomeIcon className="w-4 h-4 text-blue-600" data-id="element-5230" />
                          <span className="font-bold text-gray-900" data-id="element-5231">
                            {viewing.propertyId}
                          </span>
                          {viewing.propertyId === lead.propertyId && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-bold" data-id="element-5232">
                              Proprietate Inițială
                            </span>}
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-5233">
                          {getViewingStatusBadge(viewing.status)}
                          {viewing.id && <button onClick={() => handleStartEditViewing(viewing)} className="p-1 hover:bg-blue-100 rounded transition-colors" title="Editează" data-id="element-5234">
                              <EditIcon className="w-4 h-4 text-blue-600" data-id="element-5235" />
                            </button>}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3" data-id="element-5236">
                        <p className="text-xs text-gray-500 mb-2 font-semibold" data-id="element-5237">
                          Proprietar
                        </p>
                        <div className="space-y-1" data-id="element-5238">
                          <div className="flex items-center space-x-2 text-sm" data-id="element-5239">
                            <UserIcon className="w-3 h-3 text-gray-400" data-id="element-5240" />
                            <span className="font-medium text-gray-900" data-id="element-5241">
                              {viewing.ownerName}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm" data-id="element-5242">
                            <PhoneIcon className="w-3 h-3 text-gray-400" data-id="element-5243" />
                            <span className="text-gray-600" data-id="element-5244">
                              {viewing.ownerPhone}
                            </span>
                          </div>
                        </div>
                      </div>

                      {viewing.status === 'scheduled' && <>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2" data-id="element-5245">
                            <div className="flex items-center space-x-1" data-id="element-5246">
                              <CalendarIcon className="w-3 h-3" data-id="element-5247" />
                              <span data-id="element-5248">{viewing.scheduledDate}</span>
                            </div>
                            <div className="flex items-center space-x-1" data-id="element-5249">
                              <ClockIcon className="w-3 h-3" data-id="element-5250" />
                              <span data-id="element-5251">{viewing.scheduledTime}</span>
                            </div>
                          </div>
                          {viewing.commissionPaidBy && <div className="mb-2" data-id="element-5252">
                              <p className="text-xs text-gray-500 mb-1" data-id="element-5253">
                                Comisionare
                              </p>
                              {getCommissionBadge(viewing.commissionPaidBy, viewing.ownerCommission, viewing.clientCommission)}
                            </div>}
                        </>}

                      {viewing.notes && <div className="bg-gray-50 rounded p-2 mt-2" data-id="element-5254">
                          <p className="text-xs text-gray-600" data-id="element-5255">
                            {viewing.notes}
                          </p>
                        </div>}

                      {viewing.status === 'none' && <div className="mt-2" data-id="element-5256">
                          <button onClick={() => {
                    setNewViewingPropertyId(viewing.propertyId);
                    setNewViewingOwnerName(viewing.ownerName);
                    setNewViewingOwnerPhone(viewing.ownerPhone);
                    setIsSchedulingViewing(true);
                  }} className="text-xs text-blue-600 font-semibold hover:underline" data-id="element-5257">
                            Programează vizionare →
                          </button>
                        </div>}
                    </>}
                </motion.div>)}
            </div>

            {/* Schedule New Viewing Form - keep existing with updated styling */}
            {isSchedulingViewing && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-200" data-id="element-5258">
                <h6 className="font-bold text-gray-900 mb-3" data-id="element-5259">
                  Programează Vizionare Nouă
                </h6>

                <div className="space-y-3" data-id="element-5260">
                  {/* Client Info (Read-only) */}
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200" data-id="element-5261">
                    <p className="text-xs font-semibold text-green-900 mb-2" data-id="element-5262">
                      Informații Client
                    </p>
                    <div className="space-y-1 text-sm" data-id="element-5263">
                      <div className="flex justify-between" data-id="element-5264">
                        <span className="text-gray-600" data-id="element-5265">ID Lead:</span>
                        <span className="font-bold text-gray-900" data-id="element-5266">
                          {lead.leadId}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-5267">
                        <span className="text-gray-600" data-id="element-5268">Nume:</span>
                        <span className="font-bold text-gray-900" data-id="element-5269">
                          {lead.fullName || lead.name}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-5270">
                        <span className="text-gray-600" data-id="element-5271">Telefon:</span>
                        <span className="font-bold text-gray-900" data-id="element-5272">
                          {lead.phone}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Property ID */}
                  <div data-id="element-5273">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5274">
                      ID Proprietate *
                    </label>
                    <input type="text" value={newViewingPropertyId} onChange={e => setNewViewingPropertyId(e.target.value)} placeholder="ex: P98772" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5275" />
                  </div>

                  {/* Owner Info */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-5276">
                    <div data-id="element-5277">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5278">
                        Nume Proprietar *
                      </label>
                      <input type="text" value={newViewingOwnerName} onChange={e => setNewViewingOwnerName(e.target.value)} placeholder="ex: Ion Popescu" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5279" />
                    </div>
                    <div data-id="element-5280">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5281">
                        Telefon Proprietar *
                      </label>
                      <input type="tel" value={newViewingOwnerPhone} onChange={e => setNewViewingOwnerPhone(e.target.value)} placeholder="ex: 0721234567" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5282" />
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-5283">
                    <div data-id="element-5284">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5285">
                        Data *
                      </label>
                      <input type="date" value={newViewingDate} onChange={e => setNewViewingDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5286" />
                    </div>
                    <div data-id="element-5287">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5288">
                        Ora *
                      </label>
                      <input type="time" value={newViewingTime} onChange={e => setNewViewingTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5289" />
                    </div>
                  </div>

                  {/* Commission Selection */}
                  <div data-id="element-5290">
                    <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-5291">
                      Comisionare Părți *
                    </label>
                    <div className="grid grid-cols-3 gap-2" data-id="element-5292">
                      <button type="button" onClick={() => setNewViewingCommission('owner')} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${newViewingCommission === 'owner' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-5293">
                        Proprietar
                      </button>
                      <button type="button" onClick={() => setNewViewingCommission('client')} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${newViewingCommission === 'client' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-5294">
                        Client
                      </button>
                      <button type="button" onClick={() => setNewViewingCommission('both')} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${newViewingCommission === 'both' ? 'bg-orange-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-5295">
                        Ambele
                      </button>
                    </div>
                  </div>

                  {/* Commission Values - Dynamic based on selection */}
                  {(newViewingCommission === 'owner' || newViewingCommission === 'both') && <div data-id="element-5296">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5297">
                        Comision Proprietar (%) *
                      </label>
                      <div className="relative" data-id="element-5298">
                        <input type="number" step="0.5" min="0" max="100" value={newViewingOwnerCommission} onChange={e => setNewViewingOwnerCommission(e.target.value)} placeholder="ex: 50" className="w-full px-3 py-2 pr-8 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50" data-id="element-5299" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-600 font-bold text-sm" data-id="element-5300">
                          %
                        </span>
                      </div>
                    </div>}

                  {(newViewingCommission === 'client' || newViewingCommission === 'both') && <div data-id="element-5301">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5302">
                        Comision Client (%) *
                      </label>
                      <div className="relative" data-id="element-5303">
                        <input type="number" step="0.5" min="0" max="100" value={newViewingClientCommission} onChange={e => setNewViewingClientCommission(e.target.value)} placeholder="ex: 50" className="w-full px-3 py-2 pr-8 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50" data-id="element-5304" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 font-bold text-sm" data-id="element-5305">
                          %
                        </span>
                      </div>
                    </div>}

                  {/* Notes */}
                  <div data-id="element-5306">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5307">
                      Notițe (opțional)
                    </label>
                    <textarea value={newViewingNotes} onChange={e => setNewViewingNotes(e.target.value)} rows={2} placeholder="Detalii suplimentare despre vizionare..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5308" />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2" data-id="element-5309">
                    <button onClick={handleScheduleViewing} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors" data-id="element-5310">
                      Confirmă Programare
                    </button>
                    <button onClick={() => {
                  setIsSchedulingViewing(false);
                  setNewViewingPropertyId('');
                  setNewViewingOwnerName('');
                  setNewViewingOwnerPhone('');
                  setNewViewingDate('');
                  setNewViewingTime('');
                  setNewViewingCommission('owner');
                  setNewViewingOwnerCommission('');
                  setNewViewingClientCommission('');
                  setNewViewingNotes('');
                }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors" data-id="element-5311">
                      Anulează
                    </button>
                  </div>
                </div>
              </motion.div>}
          </motion.div>

          {/* Call Center Memo - matching dashboard style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-5312">
            <h5 className="text-lg font-semibold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-5313">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-5314">
                <ClockIcon className="w-5 h-5 text-purple-600" data-id="element-5315" />
              </div>
              <span data-id="element-5316">Memo Call Center</span>
            </h5>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5317">
              <p className="text-sm text-slate-700" data-id="element-5318">{lead.callCenterMemo}</p>
            </div>
          </motion.div>

          {/* Action History - matching dashboard style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-5319">
            <h5 className="text-lg font-semibold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-5320">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-5321">
                <CheckCircleIcon className="w-5 h-5 text-blue-600" data-id="element-5322" />
              </div>
              <span data-id="element-5323">Istoric Acțiuni</span>
            </h5>
            <div className="space-y-3" data-id="element-5324">
              {lead.actionHistory.map((action, idx) => <div key={idx} className="flex items-start space-x-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-5325">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-5326">
                    <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-5327" />
                  </div>
                  <div className="flex-1" data-id="element-5328">
                    <p className="text-sm font-semibold text-[#132C53] mb-1" data-id="element-5329">
                      {action.action}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-5330">
                      {action.timestamp} • {action.agent}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>;
}
const getCommissionBadge = (paidBy: 'owner' | 'client' | 'both', ownerCommission?: number, clientCommission?: number) => {
  const config = {
    owner: {
      label: 'Proprietar',
      color: 'bg-purple-100 text-purple-700'
    },
    client: {
      label: 'Client',
      color: 'bg-blue-100 text-blue-700'
    },
    both: {
      label: 'Ambele părți',
      color: 'bg-orange-100 text-orange-700'
    }
  };
  const {
    label,
    color
  } = config[paidBy];
  let commissionText = label;
  if (paidBy === 'owner' && ownerCommission) {
    commissionText = `${label}: ${ownerCommission}%`;
  } else if (paidBy === 'client' && clientCommission) {
    commissionText = `${label}: ${clientCommission}%`;
  } else if (paidBy === 'both' && ownerCommission && clientCommission) {
    commissionText = `P: ${ownerCommission}% / C: ${clientCommission}%`;
  }
  return <span className={`px-2 py-1 rounded-full text-xs font-bold ${color} flex items-center space-x-1`} data-id="element-5331">
      <DollarSignIcon className="w-3 h-3" data-id="element-5332" />
      <span data-id="element-5333">{commissionText}</span>
    </span>;
};